import { Injectable } from "@angular/core";
import { EDatabaseRole } from "@calaosoft/osapp-common/store/models/edatabase-role";
import { IDataSource } from "@calaosoft/osapp-common/store/models/IDataSource";
import { IStoreDocument } from "@calaosoft/osapp-common/store/models/istore-document";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { EPrefix } from "@calaosoft/osapp-common/utils/models/EPrefix";
import { firstValueFrom, Observable } from "rxjs";
import { map } from "rxjs/operators";
import { Store } from "../../../../services/store.service";
import { IStoreCategory } from "../counting/models/IStoreCategory";
import { IStoreCategoryGroup } from "../counting/models/IStoreCategoryGroup";

@Injectable()
export class CategoryService {
	//#region FIELDS

	/** Préfixe d'un objet `IStoreCategory`. */
	public static readonly C_CATEGORY_PREFIX: EPrefix = EPrefix.category;
	/** Préfixe d'un objet `IStoreGroupCategory`. */
	public static readonly C_GROUP_CATEGORY_PREFIX: EPrefix = EPrefix.categoryGroup;

	//#endregion

	//#region METHODS

	public constructor(private readonly isvcStore: Store) {}

	/** Retourne une HashMap où la clé est l'id de la catégorie et la valeur est l'objet `Category` issue de la base de données. */
	public getMapFromIdsAsync(paIds: Array<string | null>): Promise<Map<string, IStoreCategory>> {
		return firstValueFrom(
			this.isvcStore.get<IStoreCategory>({
				databasesIds: this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace),
				viewParams: {
					keys: paIds.filter((psId: string | null) => !!psId) as string[],
					include_docs: true
				}
			})
		).then((paCategories: IStoreCategory[]) => {
			// On filtre les identifiants qu'on a pas demandés.
			return this.getMapFromCategoriesArray(
				paCategories.filter((poCategory: IStoreCategory) => paIds.includes(poCategory._id))
			);
		});
	}

	/** Retourne une HashMap où la clé est l'id de la catégorie et la valeur est l'objet `Category` issue de la base de données. */
	public getMap$(): Observable<Map<string, IStoreCategory>> {
		return this.isvcStore
			.get<IStoreCategory>({
				databasesIds: this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace),
				viewParams: {
					startkey: `${EPrefix.category}`,
					endkey: `${EPrefix.category}${Store.C_ANYTHING_CODE_ASCII}`,
					include_docs: true
				}
			})
			.pipe(
				map((paCategories: IStoreCategory[]) => {
					// On filtre les identifiants qu'on a pas demandé.
					return this.getMapFromCategoriesArray(paCategories);
				})
			);
	}

	private getMapFromCategoriesArray(paCategories: IStoreCategory[]): Map<string, IStoreCategory> {
		const loMap = new Map<string, IStoreCategory>();

		for (let i = 0; i < paCategories.length; i++) {
			loMap.set(paCategories[i]._id, paCategories[i]);
		}

		return loMap;
	}

	/** Récupère les catégories souhaitées depuis la base de données, toutes si aucun paramètre.
	 * @param paIds Tableau des identifiants de catégorie à récupérer.
	 */
	public getCategoriesAsync(paIds?: string[]): Promise<IStoreCategory[]> {
		return this.getAsync<IStoreCategory>(CategoryService.C_CATEGORY_PREFIX, paIds);
	}

	/** Récupère les groupes de catégories souhaités depuis la base de données, tous si aucun paramètre.
	 * @param paIds Tableau des identifiants de groupes de catégories à récupérer.
	 */
	public getCategoryGroupsAsync(paIds?: string[]): Promise<IStoreCategoryGroup[]> {
		return this.getAsync<IStoreCategoryGroup>(CategoryService.C_GROUP_CATEGORY_PREFIX, paIds);
	}

	/** Récupère des documents en base de données en fonction d'un tableau d'identifiants, tous les documents avec un préfixe spécifié si tableau non valide.
	 * @param pePrefix Préfixe permettant de récupérer des données spécifiques.
	 * @param paIds Tableau des identifiants qu'il faut récupérer.
	 */
	private getAsync<T extends IStoreDocument>(pePrefix: EPrefix, paIds?: string[]): Promise<T[]> {
		let lsStartKey: string | undefined;
		let lsEndKey: string | undefined;

		if (!ArrayHelper.hasElements(paIds)) {
			lsStartKey = pePrefix;
			lsEndKey = `${pePrefix}${Store.C_ANYTHING_CODE_ASCII}`;
		}

		const loDataSource: IDataSource = {
			databasesIds: this.isvcStore.getDatabasesIdsByRole(EDatabaseRole.workspace),
			viewParams: {
				keys: paIds,
				startkey: lsStartKey,
				endkey: lsEndKey,
				include_docs: true
			}
		};

		return firstValueFrom(this.isvcStore.get<T>(loDataSource));
	}

	/** Retourne le tableau d'identifiant des enfants du catgroup passé en paramètre.
	 * @param paCategoryGroups Tableau de tous les groupes de catégories.
	 * @param psCategoryId Identifiant de la catégorie à rechercher.
	 */
	public getCategoryGroupChildren(paCategoryGroups: IStoreCategoryGroup[], psCategoryId: string): string[] {
		return paCategoryGroups.find((poCatGroup: IStoreCategoryGroup) => poCatGroup._id === psCategoryId)?.children ?? [];
	}

	/** Retourne `true` si tous les identifiants du tableau sont des identifiants de catégorie, sinon false.
	 * @param paCategoriesIds Tableau des catégories.
	 */
	public areCategoryIds(paCategoriesIds: string[]): boolean {
		return paCategoriesIds.every((psCategoryId: string) => this.isCategoryId(psCategoryId) === true);
	}

	/** Retourne `true` si l'identifiant de la catégorie commence par `cat_`, sinon false.
	 * @param psCategoryId Identifiant de la catégorie.
	 */
	public isCategoryId(psCategoryId: string): boolean {
		return psCategoryId.startsWith(CategoryService.C_CATEGORY_PREFIX);
	}

	/** Retourne tous les identifiants de catégories enfants d'un groupe. */
	public getChildrenIds(poGroupToFindChildren: IStoreCategoryGroup, paAllGroups: IStoreCategoryGroup[]): string[] {
		const laCategoryIds: string[] = [];

		poGroupToFindChildren.children.forEach((pschildId: string) => {
			if (pschildId.startsWith(EPrefix.categoryGroup)) {
				// Si l'enfant est un groupe de catégories, on continue le parcours.
				const loChildGroup: IStoreCategoryGroup | undefined = paAllGroups.find(
					(poGroup: IStoreCategoryGroup) => poGroup._id === pschildId
				);
				if (loChildGroup) {
					laCategoryIds.push(...this.getChildrenIds(loChildGroup, paAllGroups));
				}
			} else if (pschildId.startsWith(EPrefix.category)) {
				// Si c'est une catégorie simple, on l'ajoute au résultat.
				laCategoryIds.push(pschildId);
			}
		});

		return laCategoryIds;
	}

	//#endregion
}
